import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "../lib/api";
import { useT } from "../i18n/I18nProvider";

export default function NewsletterForm({ source = "homepage", compact = false }) {
  const { t, lp } = useT();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error(t("newsletter.consentError")); return; }
    setLoading(true);
    try {
      const res = await subscribeNewsletter({ email, consent, source });
      if (res.status === "already_confirmed") toast.info(t("newsletter.alreadyConfirmed"));
      else { toast.success(t("newsletter.success")); setDone(true); }
      if (res.dev_link) console.log("[DEV] Confirmation link:", res.dev_link);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("newsletter.errorGeneric"));
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className={`${compact ? "" : "bg-emerald-50 border border-emerald-200 rounded-2xl p-6"} flex items-start gap-3`} data-testid="newsletter-success">
        <CheckCircle2 className={`${compact ? "text-emerald-300" : "text-emerald-600"} w-5 h-5 flex-shrink-0 mt-0.5`} />
        <div className={compact ? "text-slate-300 text-sm" : "text-emerald-900"}>
          <p className="font-medium">{t("newsletter.almost")}</p>
          <p className={compact ? "text-slate-400 text-xs mt-0.5" : "text-sm mt-1 text-emerald-700"}>
            {t("newsletter.almostSub")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={compact ? "" : "bg-white rounded-2xl"} data-testid="newsletter-form">
      {!compact && (
        <div className="mb-4">
          <h3 className="font-display font-semibold text-xl text-slate-900 mb-1">{t("newsletter.title")}</h3>
          <p className="text-sm text-slate-600">{t("newsletter.sub")}</p>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${compact ? "text-slate-500" : "text-slate-400"}`} />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("newsletter.placeholder")}
            className={`w-full pl-9 pr-3 py-2.5 text-sm rounded-full outline-none transition ${
              compact
                ? "bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:border-slate-500"
                : "bg-slate-50 border border-slate-200 focus:bg-white focus:ring-2 focus:ring-[#0056D2] focus:border-transparent"
            }`}
            data-testid="newsletter-email-input"
          />
        </div>
        <button type="submit" disabled={loading} className="bg-[#0056D2] hover:bg-[#0047AD] text-white text-sm font-medium px-5 py-2.5 rounded-full transition disabled:opacity-60 flex items-center justify-center gap-2" data-testid="newsletter-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("newsletter.submit")}
        </button>
      </div>
      <label className={`flex items-start gap-2 mt-3 text-xs ${compact ? "text-slate-400" : "text-slate-600"}`}>
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#0056D2]" data-testid="newsletter-consent" />
        <span>
          {t("newsletter.consent")}{" "}
          <Link to={lp("/confidentialite")} className={compact ? "underline text-slate-300" : "underline text-[#0056D2]"}>{t("newsletter.consentLink")}</Link>{" "}
          {t("newsletter.consentEnd")}
        </span>
      </label>
    </form>
  );
}
